import React, { useState, useEffect } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import {
    Box,
    Text,
    Paper,
    TextInput,
    NumberInput,
    Select,
    SimpleGrid,
    Flex,
    Group,
    Button,
    Image,
    Stack,
    ActionIcon,
    Container,
    FileButton,
    UnstyledButton,
    InputWrapper,
    Badge
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { openConfirmModal } from '@mantine/modals'
import { useLocalStorage, useDebouncedCallback } from '@mantine/hooks'
import { IconPlus, IconTrash, IconDeviceFloppy, IconX } from '@tabler/icons-react'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { useNavigate } from 'react-router-dom'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
    ClassicEditor,
    Bold,
    Essentials,
    Italic,
    Mention,
    Paragraph,
    Undo,
    Font,
    Heading,
    List,
    Alignment,
    Link,
    PasteFromOffice,
    ImageInsertViaUrl,
    Image as CKImage,
    ImageInsert,
    Table,
    TableToolbar,
    Base64UploadAdapter,
} from 'ckeditor5'

import { addProduct, addImageProductUpload, setMainImage } from '../../../../services/products'
import { getCategorysList } from '../../../../services/category'
import { getAllSupplierList, getSupplierDetail } from '../../../../services/supplier'
import { getAllBrandList } from '../../../../services/brands'
import { getAllWarehouseList } from '../../../../services/warehouse'
import { getAllCountryList, getAllProvinceList, getAllCityList, getAllDistrictList } from '../../../../services/location'
import { notificationError, loadingNotification, completedLoadingNotification } from '../../../../component/ui/Notifications'
import NoImage from '../../../../assets/img/images/no-images.png'
import ProductVariantsSection from './ProductVariantsSection'

const CURRENCIES = [
    { value: 'AUD', label: 'AUD' },
    { value: 'CNY', label: 'CNY' },
    { value: 'EUR', label: 'EUR' },
    { value: 'IDR', label: 'IDR' },
    { value: 'INR', label: 'INR' },
    { value: 'JPY', label: 'JPY' },
    { value: 'KRW', label: 'KRW' },
    { value: 'MYR', label: 'MYR' },
    { value: 'PHP', label: 'PHP' },
    { value: 'SGD', label: 'SGD' },
    { value: 'THB', label: 'THB' },
    { value: 'USD', label: 'USD' },
]

const QUANTITY_UNIT = [
    { value: 'pcs', label: 'Pcs' },
    { value: 'kg', label: 'Kg' },
    { value: 'mtr', label: 'Mtr' },
    { value: 'ltr', label: 'Ltr' },
    { value: 'set', label: 'Set' },
    { value: 'pack', label: 'Pack' },
    { value: 'pak', label: 'Pak' },
    { value: 'box', label: 'Box' },
    { value: 'rol', label: 'Rol' },
    { value: 'crt', label: 'Crt' },
    { value: 'lsn', label: 'Lsn' },
    { value: 'bag', label: 'Bag' },
    { value: 'sheets', label: 'Sheets' },
    { value: 'dzn', label: 'Dzn' },
    { value: 'ton', label: 'Ton' },
]

const STICKY_FOOTER_HEIGHT = 80

const AddProduct = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [persistedForm, setPersistedForm] = useLocalStorage({
        key: 'add-product-manual-form',
        defaultValue: null,
    })

    const form = useForm({
        initialValues: persistedForm || {
            title: '',
            category_lvl1: null,
            category_lvl2: null,
            category_lvl3: null,
            categories: [],
            sku: '',
            ali_sku: '',
            base_qty_review: 0,
            base_average_review_point: 0,
            base_qty_sold: 0,
            group_qty: 0,
            original_currency: 'IDR',
            regular_price: 0,
            sale_price: 0,
            reseller_price: 0,
            quantity: 0,
            minimum_qty: 1,
            quantity_unit: 'pcs',
            weight: 0.1,
            weight_unit: 'kg',
            supplier_id: null,
            country_id: null,
            province_id: null,
            city_id: null,
            district_id: 0,
            dimensions: [
                { label: 'width', value: 0 },
                { label: 'length', value: 0 },
                { label: 'height', value: 0 },
            ],
            dimension_unit: 'cm',
            qty_per_cbm: 0,
            attributes: [],
            skus: [],
            video_url: '',
            description: '',
            note: '',
            original_language: null,
            original_description: null,
            original_url: null,
            wholesale_prices: [],
            brand_id: null,
            original_country: null,
            warehouses: null,
            visibility: true,
            status: 'pass',
            reward_point: 0,
        },
        validate: {
            title: (val) => (!val ? 'Product Name is required' : null),
            description: (val) => (!val ? 'Description is required' : null),
            category_lvl1: (val) => (!val ? 'Category Level 1 is required' : null),
            supplier_id: (val) => (!val ? 'Supplier is required' : null),
            country_id: (val) => (!val ? 'Country is required' : null),
            province_id: (val) => (!val ? 'Province is required' : null),
            city_id: (val) => (!val ? 'City is required' : null),
            ali_sku: (val) => (!val ? 'SKU is required' : null),
        },
    })

    // Variants State
    const [variantData, setVariantData] = useState({ skus: [], attributes: [] })

    // Persist form changes
    useEffect(() => {
        setPersistedForm(form.values)
    }, [form.values, setPersistedForm])

    // States for lists
    const [loading, setLoading] = useState(false)
    const [suppliers, setSuppliers] = useState([])
    const [brands, setBrands] = useState([])
    const [warehouses, setWarehouses] = useState([])
    const [warehouseList, setWarehouseList] = useState([]) // Store full warehouse data
    const [catLvl1, setCatLvl1] = useState([])
    const [catLvl2, setCatLvl2] = useState([])
    const [catLvl3, setCatLvl3] = useState([])
    const [countries, setCountries] = useState([])
    const [provinces, setProvinces] = useState([])
    const [cities, setCities] = useState([])
    const [districts, setDistricts] = useState([])

    // Search states for fetchers
    const [searchSupplier, setSearchSupplier] = useState('')
    const [searchBrand, setSearchBrand] = useState('')

    // Images state
    const [images, setImages] = useState([])
    const [mainImageIndex, setMainImageIndex] = useState(0)

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Product', route: '/product/all' },
        { label: 'Add New Product', route: null },
    ]

    useEffect(() => {
        dispatch(updateRoute({ data: mappingRoute }))
        fetchBaseData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchBaseData = async () => {
        fetchCountries()
        fetchSuppliers()
        fetchBrands()
        fetchWarehouses()
        fetchCategoriesLvl1()
    }

    // --- Data Fetchers ---
    const fetchCountries = async () => {
        try {
            const res = await getAllCountryList({ 'page[limit]': 300, 'fields[country]': 'name' })
            setCountries(res.data.map(c => ({ value: `${c.attributes.id}`, label: c.attributes.name })))
        } catch (e) { console.error(e) }
    }

    const fetchProvinces = async (countryId) => {
        if (!countryId) return
        try {
            const res = await getAllProvinceList({ 'filter[countries.id][is]': countryId, 'page[limit]': 1000 })
            setProvinces(res.data.map(p => ({ value: `${p.attributes.id}`, label: p.attributes.name })))
        } catch (e) { console.error(e) }
    }

    const fetchCities = async (provinceId) => {
        if (!provinceId) return
        try {
            const res = await getAllCityList({ 'filter[provinces.id][is]': provinceId, 'page[limit]': 1000 })
            setCities(res.data.map(c => ({ value: `${c.attributes.id}`, label: c.attributes.name })))
        } catch (e) { console.error(e) }
    }

    const fetchDistricts = async (cityId) => {
        if (!cityId) return
        try {
            const res = await getAllDistrictList({ 'filter[cities.id][is]': cityId, 'page[limit]': 1000 })
            setDistricts(res.data.map(d => ({ value: `${d.attributes.id}`, label: d.attributes.name })))
        } catch (e) { console.error(e) }
    }

    const fetchCategoriesLvl1 = async () => {
        try {
            const res = await getCategorysList({ 'filter[level][is]': 1, 'page[limit]': 100 })
            setCatLvl1(res.data.data.map(c => ({ value: `${c.attributes.id}`, label: c.attributes.name, image: c.attributes.image })))
        } catch (e) { console.error(e) }
    }

    const fetchCategoriesLvl2 = async (parentId) => {
        if (!parentId) return
        try {
            const res = await getCategorysList({ 'filter[parent_id][is]': parentId, 'page[limit]': 100 })
            setCatLvl2(res.data.data.map(c => ({ value: `${c.attributes.id}`, label: c.attributes.name, image: c.attributes.image })))
        } catch (e) { console.error(e) }
    }

    const fetchCategoriesLvl3 = async (parentId) => {
        if (!parentId) return
        try {
            const res = await getCategorysList({ 'filter[parent_id][is]': parentId, 'page[limit]': 100 })
            setCatLvl3(res.data.data.map(c => ({ value: `${c.attributes.id}`, label: c.attributes.name, image: c.attributes.image })))
        } catch (e) { console.error(e) }
    }

    const fetchSuppliers = useDebouncedCallback(async () => {
        try {
            const res = await getAllSupplierList({ 'filter[name][like]': searchSupplier, 'page[limit]': 20 })
            setSuppliers(res.data.map(s => ({ value: `${s.attributes.id}`, label: s.attributes.name })))
        } catch (e) { console.error(e) }
    }, 300)

    const fetchBrands = useDebouncedCallback(async () => {
        try {
            const res = await getAllBrandList({ 'filter[name][like]': searchBrand, 'page[limit]': 20 })
            setBrands(res.data.map(b => ({ value: `${b.attributes.id}`, label: b.attributes.name })))
        } catch (e) { console.error(e) }
    }, 300)

    const fetchWarehouses = async () => {
        try {
            const res = await getAllWarehouseList({ 'page[limit]': 100 })
            setWarehouseList(res.data)
            setWarehouses(res.data.map(w => ({ value: `${w.attributes.id}`, label: w.attributes.name })))
        } catch (e) { console.error(e) }
    }

    // --- Listeners for Cascading selects ---
    useEffect(() => {
        if (form.values.country_id) fetchProvinces(form.values.country_id)
    }, [form.values.country_id])

    useEffect(() => {
        if (form.values.province_id) fetchCities(form.values.province_id)
    }, [form.values.province_id])

    useEffect(() => {
        if (form.values.city_id) fetchDistricts(form.values.city_id)
    }, [form.values.city_id])

    useEffect(() => {
        if (form.values.category_lvl1) fetchCategoriesLvl2(form.values.category_lvl1)
    }, [form.values.category_lvl1])

    useEffect(() => {
        if (form.values.category_lvl2) fetchCategoriesLvl3(form.values.category_lvl2)
    }, [form.values.category_lvl2])

    useEffect(() => {
        fetchSuppliers()
    }, [searchSupplier, fetchSuppliers])

    useEffect(() => {
        fetchBrands()
    }, [searchBrand, fetchBrands])

    // --- Handlers ---
    const renderSelectOption = ({ option }) => (
        <Group flex="1" gap="xs">
            <Paper shadow="sm" w={32} withBorder radius='md' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <Image src={option.image || NoImage} w={30} h={30} radius="sm" fallbackSrc={NoImage} />
            </Paper>
            <Text size="sm">{option.label}</Text>
        </Group>
    )

    const handleRemoveImage = (index) => {
        const newImages = [...images]
        newImages.splice(index, 1)
        setImages(newImages)

        if (index === mainImageIndex) {
            setMainImageIndex(0)
        } else if (index < mainImageIndex) {
            setMainImageIndex(mainImageIndex - 1)
        }
    }

    const handleSetMainImage = (index) => {
        setMainImageIndex(index)
    }

    const handleSupplierChange = async (val) => {
        if (!val) return

        try {
            const res = await getSupplierDetail(val)
            const supplierAttributes = res.data.attributes

            if (supplierAttributes.country_id) {
                openConfirmModal({
                    title: 'Auto-fill Location',
                    centered: true,
                    children: (
                        <Text size="sm">
                            Do you want to use the supplier's location (Country, City, Province) for this product?
                        </Text>
                    ),
                    labels: { confirm: 'Yes, use supplier location', cancel: 'No' },
                    onConfirm: () => {
                        form.setFieldValue('country_id', String(supplierAttributes.country_id))
                        if (supplierAttributes.province_id) form.setFieldValue('province_id', String(supplierAttributes.province_id))
                        if (supplierAttributes.city_id) form.setFieldValue('city_id', String(supplierAttributes.city_id))
                        if (supplierAttributes.district_id) form.setFieldValue('district_id', String(supplierAttributes.district_id))
                    }
                })
            }
        } catch (e) {
            console.error(e)
        }
    }

    const handleWarehouseChange = (val) => {
        if (!val) return
        const selectedWarehouse = warehouseList.find(w => String(w.attributes.id) === String(val))
        if (!selectedWarehouse) return

        const attrs = selectedWarehouse.attributes
        if (attrs.country) {
            openConfirmModal({
                title: 'Auto-fill Location',
                centered: true,
                children: (
                    <Text size="sm">
                        Do you want to use the warehouse's location (Country, City, Province) for this product?
                    </Text>
                ),
                labels: { confirm: 'Yes, use warehouse location', cancel: 'No' },
                onConfirm: () => {
                    form.setFieldValue('country_id', String(attrs.country))
                    if (attrs.province) form.setFieldValue('province_id', String(attrs.province))
                    if (attrs.city) form.setFieldValue('city_id', String(attrs.city))
                    if (attrs.district) form.setFieldValue('district_id', String(attrs.district))
                }
            })
        }
    }

    const handleSubmit = async (values) => {
        if (images.length === 0) {
            notificationError('Required Field', 'Please upload at least one product image.')
            return
        }

        setLoading(true)
        const toastId = loadingNotification('create-product', 'Creating Product', 'Please wait while we create your product...')

        try {
            // Prepare categories array
            const productCategories = [
                values.category_lvl1,
                values.category_lvl2,
                values.category_lvl3
            ].filter(Boolean).map(id => Number(id))

            const payload = {
                ...values,
                categories: productCategories,
                supplier_id: Number(values.supplier_id),
                brand_id: values.brand_id ? Number(values.brand_id) : null,
                country_id: Number(values.country_id),
                province_id: Number(values.province_id),
                city_id: Number(values.city_id),
                district_id: Number(values.district_id),
                warehouses: Array.isArray(values.warehouses)
                    ? values.warehouses.map(id => Number(id))
                    : (values.warehouses ? [Number(values.warehouses)] : []),
            }

            // Cleanup some helper fields before sending
            delete payload.category_lvl1
            delete payload.category_lvl2
            delete payload.category_lvl3

            // Inject Variants
            if (variantData.skus.length > 0) {
                payload.skus = variantData.skus
                payload.attributes = variantData.attributes
            }

            const res = await addProduct(payload)
            if (res.success) {
                const productId = res.data.attributes.id

                // Upload images
                const formData = new FormData()
                images.forEach((file) => {
                    formData.append('images[]', file)
                })

                const resImg = await addImageProductUpload(productId, formData)

                if (resImg.success) {
                    // Set main image if possible
                    try {
                        const uploadedImages = resImg.data
                        if (Array.isArray(uploadedImages) && uploadedImages[mainImageIndex]) {
                            const imgId = uploadedImages[mainImageIndex].id || uploadedImages[mainImageIndex].attributes?.id
                            if (imgId) {
                                await setMainImage(productId, { image_id: imgId })
                            }
                        }
                    } catch (setMainErr) {
                        console.error('Failed to set main image', setMainErr)
                    }

                    completedLoadingNotification(toastId, 'Success', 'Product created and images uploaded successfully.')
                    setPersistedForm(null) // Clear persistence
                    navigate(`/product/${productId}`)
                } else {
                    completedLoadingNotification(toastId, 'Partial Success', 'Product created, but image upload failed.')
                    navigate(`/product/${productId}`)
                }
            }
        } catch (e) {
            console.error(e)
            const msg = e.response?.data?.message || 'Something went wrong while creating the product.'
            completedLoadingNotification(toastId, 'Error', msg, 'error')
        } finally {
            setLoading(false)
        }
    }

    const handleValidationErrors = (errors) => {
        if (Object.keys(errors).length > 0) {
            notificationError('Validation Error', 'Please check the form for errors.')
            setTimeout(() => {
                const errorElement = document.querySelector(
                    '[aria-invalid="true"], .mantine-InputWrapper-error'
                )
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
            }, 100)
        }
    }

    // --- CKEditor Helper ---
    const editorConfig = {
        plugins: [
            Bold,
            Essentials,
            Italic,
            Mention,
            Paragraph,
            Undo,
            Font,
            Heading,
            List,
            Alignment,
            Link,
            PasteFromOffice,
            ImageInsertViaUrl,
            CKImage,
            ImageInsert,
            Table, TableToolbar,
            Base64UploadAdapter,
        ],
        alignment: {
            options: ['left', 'right']
        },
        fontSize: {
            options: [
                9,
                10,
                11,
                12,
                13,
                14,
                15,
                16,
                17,
                18,
                19,
                20,
                21,
                22
            ],
            supportAllValues: true
        },
        list: {
            properties: {
                styles: true,
                startIndex: true,
                reversed: true
            }
        },
        link: {
            decorators: {
                addTargetToExternalLinks: {
                    mode: 'automatic',
                    callback: url => /^(https?:)?\/\//.test(url),
                    attributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    }
                }
            }
        },
        toolbar: {
            items: [
                'undo', 'redo',
                '|',
                'heading',
                '|',
                'fontSize', 'Bold', 'Italic', 'alignment',
                '|',
                'link',
                '|',
                'bulletedList', 'numberedList',
                '|',
                'insertImage',
                '|',
                'insertTable'
            ],
            shouldNotGroupWhenFull: true
        },
        initialData: ''
    }

    return (
        <AuthLayout>
            <Container size="lg" pt={20} pb={STICKY_FOOTER_HEIGHT + 20}>
                <Flex justify='space-between' align="center" mb={30}>
                    <Box>
                        <Text fw={700} size="xl">Add New Product Manually</Text>
                        <Text size="sm" c="dimmed">Complete the form below to create a new product entry</Text>
                    </Box>
                    <Button variant="outline" size="xs" color="gray" onClick={() => navigate('/product/all')}>Cancel</Button>
                </Flex>

                <form onSubmit={form.onSubmit(handleSubmit, handleValidationErrors)}>
                    <Stack gap="xl">
                        {/* General Information */}
                        <Paper p="xl" withBorder radius="lg" shadow="sm">
                            <Text fw={600} size="lg" mb="md">General Information</Text>
                            <Stack gap="md">
                                <TextInput
                                    label="Product Name"
                                    placeholder="Enter product title"
                                    withAsterisk
                                    {...form.getInputProps('title')}
                                />

                                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                                    <TextInput
                                        label="Original SKU"
                                        placeholder="Original SKU"
                                        {...form.getInputProps('sku')}
                                    />
                                    <TextInput
                                        label="ALI SKU"
                                        placeholder="ALI-"
                                        withAsterisk
                                        {...form.getInputProps('ali_sku')}
                                    />
                                </SimpleGrid>

                                <InputWrapper label="Description" my={10} withAsterisk error={form.errors.description}>
                                    <CKEditor
                                        editor={ClassicEditor}
                                        config={editorConfig}
                                        data={form.values.description}
                                        onReady={(editor) => {
                                            editor.editing.view.change((writer) => {
                                                writer.setStyle('min-height', '100px', editor.editing.view.document.getRoot());
                                            });
                                        }}
                                        onChange={(event, editor) => {
                                            form.setFieldValue('description', editor.getData())
                                        }}
                                    />
                                </InputWrapper>
                            </Stack>
                        </Paper>

                        {/* Source & Origin */}
                        <Paper p="xl" withBorder radius="lg" shadow="sm">
                            <Text fw={600} size="lg" mb="md">Source & Origin</Text>
                            <Stack gap="md">
                                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                                    <Select
                                        label="Supplier"
                                        placeholder="Search supplier"
                                        withAsterisk
                                        data={suppliers}
                                        searchable
                                        searchValue={searchSupplier}
                                        onSearchChange={setSearchSupplier}
                                        {...form.getInputProps('supplier_id')}
                                        onChange={(val) => {
                                            form.setFieldValue('supplier_id', val)
                                            handleSupplierChange(val)
                                        }}
                                    />
                                    <Select
                                        label="Brand"
                                        placeholder="Search brand"
                                        data={brands}
                                        searchable
                                        searchValue={searchBrand}
                                        onSearchChange={setSearchBrand}
                                        {...form.getInputProps('brand_id')}
                                    />
                                    <Select
                                        label="Warehouses"
                                        placeholder="Select warehouse"
                                        data={warehouses}
                                        searchable
                                        {...form.getInputProps('warehouses')}
                                        onChange={(val) => {
                                            form.setFieldValue('warehouses', val)
                                            handleWarehouseChange(val)
                                        }}
                                    />
                                </SimpleGrid>

                                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                                    <Select
                                        label="Country"
                                        placeholder="Select country"
                                        withAsterisk
                                        data={countries}
                                        searchable
                                        {...form.getInputProps('country_id')}
                                    />
                                    <Select
                                        label="Province"
                                        placeholder="Select province"
                                        withAsterisk
                                        data={provinces}
                                        disabled={!form.values.country_id}
                                        searchable
                                        {...form.getInputProps('province_id')}
                                    />
                                </SimpleGrid>

                                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                                    <Select
                                        label="City"
                                        placeholder="Select city"
                                        withAsterisk
                                        data={cities}
                                        disabled={!form.values.province_id}
                                        searchable
                                        {...form.getInputProps('city_id')}
                                    />
                                    <Select
                                        label="District"
                                        placeholder="Select district"
                                        data={districts}
                                        disabled={!form.values.city_id}
                                        searchable
                                        {...form.getInputProps('district_id')}
                                    />
                                </SimpleGrid>
                            </Stack>
                        </Paper>

                        {/* Product Category */}
                        <Paper p="xl" withBorder radius="lg" shadow="sm">
                            <Text fw={600} size="lg" mb="md">Product Category</Text>
                            <Stack gap="md">
                                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                                    <Select
                                        label="Category Level 1"
                                        placeholder="Select category"
                                        withAsterisk
                                        data={catLvl1}
                                        renderOption={renderSelectOption}
                                        {...form.getInputProps('category_lvl1')}
                                        onChange={(val) => {
                                            form.setFieldValue('category_lvl1', val)
                                            form.setFieldValue('category_lvl2', null)
                                            form.setFieldValue('category_lvl3', null)
                                        }}
                                    />
                                    <Select
                                        label="Category Level 2"
                                        placeholder="Select category"
                                        data={catLvl2}
                                        disabled={!form.values.category_lvl1}
                                        renderOption={renderSelectOption}
                                        {...form.getInputProps('category_lvl2')}
                                        onChange={(val) => {
                                            form.setFieldValue('category_lvl2', val)
                                            form.setFieldValue('category_lvl3', null)
                                        }}
                                    />
                                    <Select
                                        label="Category Level 3"
                                        placeholder="Select category"
                                        data={catLvl3}
                                        disabled={!form.values.category_lvl2}
                                        renderOption={renderSelectOption}
                                        {...form.getInputProps('category_lvl3')}
                                    />
                                </SimpleGrid>
                            </Stack>
                        </Paper>



                        {/* Pricing & Inventory */}
                        <Paper p="xl" withBorder radius="lg" shadow="sm">
                            <Text fw={600} size="lg" mb="md">Pricing & Inventory</Text>
                            <Stack gap="md">
                                <SimpleGrid cols={{ base: 1, sm: 4 }}>
                                    <Select
                                        label="Currency"
                                        data={CURRENCIES}
                                        {...form.getInputProps('original_currency')}
                                    />
                                    <NumberInput
                                        label="Retail Price"
                                        placeholder="0"
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        min={0}
                                        leftSection={<Text size="xs" c="dimmed">{form.values.original_currency}</Text>}
                                        rightSection={<Text size="xs" c="dimmed" mr="xs">/ {form.values.quantity_unit}</Text>}
                                        rightSectionWidth={60}
                                        {...form.getInputProps('regular_price')}
                                    />
                                    <NumberInput
                                        label="Sale Price"
                                        placeholder="0"
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        min={0}
                                        leftSection={<Text size="xs" c="dimmed">{form.values.original_currency}</Text>}
                                        rightSection={<Text size="xs" c="dimmed" mr="xs">/ {form.values.quantity_unit}</Text>}
                                        rightSectionWidth={60}
                                        {...form.getInputProps('sale_price')}
                                    />
                                    <NumberInput
                                        label="Dropship Price"
                                        placeholder="0"
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        min={0}
                                        leftSection={<Text size="xs" c="dimmed">{form.values.original_currency}</Text>}
                                        rightSection={<Text size="xs" c="dimmed" mr="xs">/ {form.values.quantity_unit}</Text>}
                                        rightSectionWidth={60}
                                        {...form.getInputProps('reseller_price')}
                                    />
                                </SimpleGrid>

                                <SimpleGrid cols={{ base: 1, sm: 4 }}>
                                    <Select
                                        label="Quantity Unit"
                                        data={QUANTITY_UNIT}
                                        {...form.getInputProps('quantity_unit')}
                                    />
                                    <NumberInput
                                        label="Quantity"
                                        placeholder="0"
                                        min={0}
                                        rightSection={<Text size="xs" c="dimmed" mr="xs">{form.values.quantity_unit}</Text>}
                                        rightSectionWidth={60}
                                        {...form.getInputProps('quantity')}
                                    />
                                    <NumberInput
                                        label="Minimum Qty"
                                        placeholder="1"
                                        min={1}
                                        rightSection={<Text size="xs" c="dimmed" mr="xs">{form.values.quantity_unit}</Text>}
                                        rightSectionWidth={60}
                                        {...form.getInputProps('minimum_qty')}
                                    />
                                    <NumberInput
                                        label="Asiacommerce Reward Point"
                                        placeholder="0"
                                        min={0}
                                        {...form.getInputProps('reward_point')}
                                    />
                                </SimpleGrid>

                                {/* <Flex gap="xl">
                                    <Switch
                                        label="Visibility"
                                        size="md"
                                        {...form.getInputProps('visibility', { type: 'checkbox' })}
                                    />
                                </Flex> */}
                            </Stack>
                        </Paper>

                        {/* Shipping & Dimensions */}
                        <Paper p="xl" withBorder radius="lg" shadow="sm">
                            <Text fw={600} size="lg" mb="md">Shipping & Dimensions</Text>
                            <Stack gap="md">
                                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                                    <NumberInput
                                        label="Weight"
                                        placeholder="0.1"
                                        decimalScale={2}
                                        rightSection={<Text size="xs" c="dimmed" mr="xs">{form.values.weight_unit}/{form.values.quantity_unit}</Text>}
                                        rightSectionWidth={60}
                                        min={0}
                                        {...form.getInputProps('weight')}
                                    />
                                    <Select
                                        label="Weight Unit"
                                        data={[{ value: 'kg', label: 'kg' }, { value: 'g', label: 'g' }]}
                                        {...form.getInputProps('weight_unit')}
                                    />
                                    <NumberInput
                                        label="Qty per CBM"
                                        placeholder="0"
                                        min={0}
                                        {...form.getInputProps('qty_per_cbm')}
                                    />
                                </SimpleGrid>

                                <Group grow align="end">
                                    {form.values.dimensions.map((dim, index) => (
                                        <NumberInput
                                            key={dim.label}
                                            label={dim.label.charAt(0).toUpperCase() + dim.label.slice(1)}
                                            placeholder="0"
                                            min={0}
                                            rightSection={<Text size="xs" c="dimmed" mr="xl">{form.values.dimension_unit}</Text>}
                                            {...form.getInputProps(`dimensions.${index}.value`)}
                                        />
                                    ))}
                                    <Select
                                        label="Dimension Unit"
                                        data={[{ value: 'cm', label: 'cm' }, { value: 'mm', label: 'mm' }, { value: 'm', label: 'm' }]}
                                        {...form.getInputProps('dimension_unit')}
                                    />
                                </Group>
                            </Stack>
                        </Paper>

                        {/* Supplier Note */}
                        <Paper p="xl" withBorder radius="lg" shadow="sm">
                            <Text fw={600} size="lg" mb="md">Supplier Note</Text>
                            <Box>
                                <Text size="sm" fw={500} mb={4}>Supplier Note</Text>
                                <CKEditor
                                    editor={ClassicEditor}
                                    onReady={(editor) => {
                                        editor.editing.view.change((writer) => {
                                            writer.setStyle('min-height', '100px', editor.editing.view.document.getRoot());
                                        });
                                    }}
                                    config={editorConfig}
                                    data={form.values.note}
                                    onChange={(event, editor) => {
                                        form.setFieldValue('Supplier Note', editor.getData())
                                    }}
                                />
                            </Box>
                        </Paper>

                        {/* Product Variants Section */}
                        <ProductVariantsSection
                            onUpdate={setVariantData}
                            currency={form.values.original_currency}
                        />

                        {/* Images */}
                        <Paper p="xl" withBorder radius="lg" shadow="sm">
                            <Text fw={600} size="lg" mb="md">Product Images</Text>
                            <Stack gap="md">
                                <SimpleGrid cols={{ base: 2, sm: 4, md: 6 }} spacing="md">
                                    {images.map((file, index) => (
                                        <Box key={index} style={{ position: 'relative', aspectRatio: '1/1' }}>
                                            <Image
                                                src={URL.createObjectURL(file)}
                                                radius="md"
                                                h="100%"
                                                w="100%"
                                                fallbackSrc={NoImage}
                                                style={{
                                                    objectFit: 'cover',
                                                    cursor: 'pointer',
                                                    border: index === mainImageIndex ? '2px solid var(--mantine-color-blue-filled)' : 'none'
                                                }}
                                                onClick={() => handleSetMainImage(index)}
                                            />
                                            {index === mainImageIndex && (
                                                <Badge
                                                    variant="filled"
                                                    color="blue"
                                                    size="xs"
                                                    style={{ position: 'absolute', top: 5, left: 5, zIndex: 5, textTransform: 'capitalize' }}
                                                >
                                                    Main Image
                                                </Badge>
                                            )}
                                            <ActionIcon
                                                color="red"
                                                variant="filled"
                                                radius="xl"
                                                size="sm"
                                                style={{ position: 'absolute', top: -8, right: -8, zIndex: 10 }}
                                                onClick={() => handleRemoveImage(index)}
                                            >
                                                <IconX size={12} />
                                            </ActionIcon>
                                        </Box>
                                    ))}

                                    <FileButton onChange={(files) => setImages([...images, ...files])} accept="image/*" multiple>
                                        {(props) => (
                                            <UnstyledButton
                                                {...props}
                                                style={{
                                                    border: '2px dashed #dee2e6',
                                                    borderRadius: '12px',
                                                    aspectRatio: '1/1',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f8f9fa',
                                                    cursor: 'pointer',
                                                    width: '100%'
                                                }}
                                            >
                                                <IconPlus size={28} stroke={1.5} color="#adb5bd" />
                                                <Text size="xs" fw={500} c="dimmed" mt={4}>Add Image</Text>
                                            </UnstyledButton>
                                        )}
                                    </FileButton>
                                </SimpleGrid>
                                <Text size="xs" color="gray">Recommended: Square images (1:1), min 800x800px.</Text>
                            </Stack>
                        </Paper>

                    </Stack>

                    {/* Floating Sticky Footer */}
                    <Box
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            borderTop: '1px solid #e0e0e0',
                            padding: '15px 30px',
                            zIndex: 100,
                            boxShadow: '0 -4px 10px rgba(0,0,0,0.05)',
                        }}
                    >
                        <Flex justify="flex-end" gap="md">
                            <Button
                                variant="subtle"
                                color="gray"
                                leftSection={<IconTrash size={16} />}
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to clear the form?')) {
                                        form.reset()
                                        setPersistedForm(null)
                                        setImages([])
                                        setMainImageIndex(0)
                                    }
                                }}
                            >
                                Clear Form
                            </Button>
                            <Button
                                type="submit"
                                loading={loading}
                                leftSection={<IconDeviceFloppy size={16} />}
                                size="md"
                                color="red"
                                radius="md"
                                px={40}
                            >
                                Save Product
                            </Button>
                        </Flex>
                    </Box>
                </form>
            </Container>
        </AuthLayout>
    )
}

export default AddProduct
